/**
 * Hero of the Metal Resources hub pages: breadcrumb, `h1` title and a description, on a tertiary
 * band. Used by `src/pages/metal-resources/references/index.tsx` and
 * `src/pages/metal-resources/tools-calculators/index.tsx` (copy in
 * `src/content/ryerson/metal-resources/tools-calculators/index.ts` → `HeroContent`).
 *
 * The description can carry an inline link: every occurrence of `actionText` inside it becomes a
 * link to `actionLink`. When the text doesn't occur, no link is drawn. In both callers' copy today
 * the description doesn't contain `actionText`, so neither hero shows a link.
 */
import React from "react";
import { ContentSection } from "@ryerson/frontend.layout";
import { Media } from "@ryerson/frontend.next";
import { Typography } from "@ryerson/frontend.typography";
import { Link } from "@ryerson/frontend.navigation";
import { css } from "@emotion/react";
import { Breadcrumb } from "@ryerson/frontend.navigation";

/** One language's hero copy. */
export type ReferencesHeroContent = {
	title: string;
	description: string;
	/** Text inside `description` to turn into a link; matched exactly, case-sensitive. */
	actionText: string;
	/** Where that inline link goes, for example `/contact-us`. */
	actionLink: string;
};

/** Props for the hero. */
export type ReferencesHeroProps = {
	content: ReferencesHeroContent;
};

/** Renders the hub-page hero; see the file header for the inline link. */
const Hero: React.FunctionComponent<ReferencesHeroProps> = ({ content }) => {
	const [cleanDescription, setCleanDescription] = React.useState<any>(content.description);

	// Splits the description on `actionText` after mount and puts a link between the parts. It
	// re-runs only when the description changes.
	React.useEffect(() => {
		let description = content.description;
		let find = content.actionText;
		let parts = description.split(find);
		const descriptionComponents: any[] = [];
		parts.forEach((part: string, index: number) => {
			descriptionComponents.push(<span>{part}</span>);
			if (parts.length - 1 !== index) {
				descriptionComponents.push(
					<Link to={content.actionLink}>{content.actionText}</Link>
				);
			}
		});
		setCleanDescription(descriptionComponents);
	}, [content.description]);

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection type="tertiary" vPadding="50px">
					<Breadcrumb type="tertiary" size="xs" />
					<Typography
						variant="h1"
						css={css`
							letter-spacing: -2px;
							margin-top: 21px;
							margin-bottom: 25px;
						`}
						type="tertiary"
					>
						{content.title}
					</Typography>
					<Typography
						css={css`
							max-width: 665px;
							display: block;
						`}
						size="md"
						type="tertiary"
					>
						{cleanDescription}
					</Typography>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="tertiary" vPadding="30px">
					<Breadcrumb type="tertiary" size="xs" />
					<Typography
						css={css`
							letter-spacing: -2px;
							margin-top: 12px;
							margin-bottom: 20px;
						`}
						variant="h1"
						type="tertiary"
					>
						{content.title}
					</Typography>
					<Typography size="md" type="tertiary">
						{cleanDescription}
					</Typography>
				</ContentSection>
			</Media>
		</>
	);
};

export default Hero;
